/// <amd-module name="redux/Utils/ReducerHelper" />
import { Action, Reducer } from "redux";

/**
 * A utility function which reduces a list of reducer functions into one root reducer function. This is similar to redux's combineReducer function,
 * the only difference is that each reducer passed in combineReducer works with only a portion of app state, while all the reducers passed in reduceReducers
 * works with the full App state. This helps when the app has too many reducer functions that works on the same state.
 * @param initialState The initial state passed to the reducer
 * @param reducers Array of reducers to combine
 * @returns A combined root reducer
 */
export function reduceReducers<S = any, A1 extends Action = Action>(initialState: S, r1: Reducer<Partial<S>, A1>): Reducer<S, A1>;
export function reduceReducers<S = any, A1 extends Action = Action, A2 extends Action = Action>(
    initialState: S,
    r1: Reducer<Partial<S>, A1>,
    r2: Reducer<Partial<S>, A2>
): Reducer<S, A1 | A2>;
export function reduceReducers<S = any, A1 extends Action = Action, A2 extends Action = Action, A3 extends Action = Action>(
    initialState: S,
    r1: Reducer<Partial<S>, A1>,
    r2: Reducer<Partial<S>, A2>,
    r3: Reducer<Partial<S>, A3>
): Reducer<S, A1 | A2 | A3>;
export function reduceReducers<
    S = any,
    A1 extends Action = Action,
    A2 extends Action = Action,
    A3 extends Action = Action,
    A4 extends Action = Action
>(
    initialState: S,
    r1: Reducer<Partial<S>, A1>,
    r2: Reducer<Partial<S>, A2>,
    r3: Reducer<Partial<S>, A3>,
    r4: Reducer<Partial<S>, A4>
): Reducer<S, A1 | A2 | A3 | A4>;
export function reduceReducers<
    S = any,
    A1 extends Action = Action,
    A2 extends Action = Action,
    A3 extends Action = Action,
    A4 extends Action = Action,
    A5 extends Action = Action
>(
    initialState: S,
    r1: Reducer<Partial<S>, A1>,
    r2: Reducer<Partial<S>, A2>,
    r3: Reducer<Partial<S>, A3>,
    r4: Reducer<Partial<S>, A4>,
    r5: Reducer<Partial<S>, A5>
): Reducer<Partial<S>, A1 | A2 | A3 | A4 | A5>;
export function reduceReducers<
    S = any,
    A1 extends Action = Action,
    A2 extends Action = Action,
    A3 extends Action = Action,
    A4 extends Action = Action,
    A5 extends Action = Action,
    A6 extends Action = Action
>(
    initialState: S,
    r1: Reducer<Partial<S>, A1>,
    r2: Reducer<Partial<S>, A2>,
    r3: Reducer<Partial<S>, A3>,
    r4: Reducer<Partial<S>, A4>,
    r5: Reducer<Partial<S>, A5>,
    r6: Reducer<Partial<S>, A6>
): Reducer<S, A1 | A2 | A3 | A4 | A5 | A6>;
export function reduceReducers<
    S = any,
    A1 extends Action = Action,
    A2 extends Action = Action,
    A3 extends Action = Action,
    A4 extends Action = Action,
    A5 extends Action = Action,
    A6 extends Action = Action,
    A7 extends Action = Action
>(
    initialState: S,
    r1: Reducer<Partial<S>, A1>,
    r2: Reducer<Partial<S>, A2>,
    r3: Reducer<Partial<S>, A3>,
    r4: Reducer<Partial<S>, A4>,
    r5: Reducer<Partial<S>, A5>,
    r6: Reducer<Partial<S>, A6>,
    r7: Reducer<Partial<S>, A7>
): Reducer<S, A1 | A2 | A3 | A4 | A5 | A6 | A7>;
export function reduceReducers<
    S = any,
    A1 extends Action = Action,
    A2 extends Action = Action,
    A3 extends Action = Action,
    A4 extends Action = Action,
    A5 extends Action = Action,
    A6 extends Action = Action,
    A7 extends Action = Action,
    A8 extends Action = Action
>(
    initialState: S,
    r1: Reducer<Partial<S>, A1>,
    r2: Reducer<Partial<S>, A2>,
    r3: Reducer<Partial<S>, A3>,
    r4: Reducer<Partial<S>, A4>,
    r5: Reducer<Partial<S>, A5>,
    r6: Reducer<Partial<S>, A6>,
    r7: Reducer<Partial<S>, A7>,
    r8: Reducer<Partial<S>, A8>
): Reducer<S, A1 | A2 | A3 | A4 | A5 | A6 | A7 | A8>;
export function reduceReducers(initialState: any, ...reducers: Reducer<any>[]): Reducer<any, any> {
    return (prevState: any, action: any) => {
        return reducers.reduce((newState, reducer) => reducer(newState, action), !prevState ? initialState : prevState);
    };
}
